import { Injectable, effect, inject, signal } from '@angular/core';
import {
  HttpTransportType,
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from '@microsoft/signalr';
import { Observable, Subject } from 'rxjs';
import { SignalrApi } from '../apis/signalr.api';
import { SiaProfileNotification } from '../interfaces/sia-profile-notifications';
import { SiaSignalrMessage } from '../interfaces/sia-signalr-message';
import { SiaSignalrStatusMessage } from '../interfaces/sia-signalr-status-message';
import { SiaTokenUser } from '../interfaces/sia-token-user';
import { SiaCacheDatabaseService } from './cache-database.service';
import { removeLocalStorageItems } from './functions-utils.service';
import { jsonDeepClone } from './jsonDeepClone';
import { SiaSessionService } from './session.service';
import { SiaTokenStoreService } from './token-store.service';
import { SiaUsersService } from './users.service';

@Injectable({
  providedIn: 'root',
})
export class SiaSignalrService {
  #siaCacheDatabaseService = inject(SiaCacheDatabaseService);
  #siaTokenStoreService = inject(SiaTokenStoreService);
  #siaSessionService = inject(SiaSessionService);
  #siaUsersService = inject(SiaUsersService);
  #signalrApi = inject(SignalrApi);

  #userId?: number = 0;
  user = signal<SiaTokenUser | null>(null);
  #justLoggedIn = false;
  private _serviceMessage$: Subject<SiaSignalrMessage> =
    new Subject<SiaSignalrMessage>();

  private _status$: Subject<SiaSignalrStatusMessage> =
    new Subject<SiaSignalrStatusMessage>();
  private _profileNotifications$: Subject<SiaProfileNotification> =
    new Subject<SiaProfileNotification>();

  #connection: HubConnection | null = null;
  #isConnecting = false;
  #hasConnectedBefore = false;
  #refreshMessages: Record<string, boolean> = {};
  #refreshTimeout: any;
  readonly #refreshMessage$ = new Subject<any>();

  #memoryToClearKeys: Record<string, boolean> = {};
  #memoryToNotifyKeys: Record<string, boolean> = {};
  #refreshCollectionsTimeout: any;
  readonly #refreshCollectionsMessage$ = new Subject<any>();

  get connectionId() {
    return (this.#connection as any)?.connection?.connectionId;
  }
  get serviceMessage$(): Observable<SiaSignalrMessage> {
    return this._serviceMessage$.asObservable();
  }

  get refreshMessage$(): Observable<any> {
    return this.#refreshMessage$.asObservable();
  }

  get refreshCollectionsMessage$(): Observable<any> {
    return this.#refreshCollectionsMessage$.asObservable();
  }

  get profileNotifications$(): Observable<SiaProfileNotification> {
    return this._profileNotifications$.asObservable();
  }
  get status$() {
    return this._status$.asObservable();
  }

  constructor() {
    effect(() => {
      const u = this.user();
      if (u && this.#justLoggedIn) {
        const state = this.getState();
        if (state === HubConnectionState.Connected) {
          this.#justLoggedIn = false;
        } else if (
          state === HubConnectionState.Disconnected ||
          state === undefined
        ) {
          this.#justLoggedIn = false;
          this.#buildConnection(u.id!, this.getApiUrl() ?? '');
          this.#startConnection();
        }
      }
    });

    setInterval(() => this.#maybeConnect(), 5000);
  }

  async #maybeConnect() {
    if (this.#isConnecting || !this.#userId) return;
    const apiUrl = this.getApiUrl();
    if (!apiUrl) return;
    const state = this.#connection?.state;
    if (
      state === HubConnectionState.Connected ||
      state === HubConnectionState.Connecting ||
      state === HubConnectionState.Reconnecting
    )
      return;
    this.#isConnecting = true;
    try {
      if (!this.#connection) this.#buildConnection(this.#userId, apiUrl);
      await this.#startConnection();
    } finally {
      this.#isConnecting = false;
    }
  }

  async #startConnection() {
    if (await this.#siaTokenStoreService.isCurrentTokenExpired()) return;
    try {
      await this.#connection!.start();
      this._status$.next({ date: new Date(), status: 'info' });
      await this.#connection!.invoke('Login', this.#userId);
    } catch (err: any) {
      this._status$.next({ date: new Date(), status: 'error' });
      console.error('SignalR connection error:', err);
      this.#connection = null;
    } finally {
      if (this.#connection?.state === HubConnectionState.Connected) {
        if (this.#hasConnectedBefore) {
          this.sendAllMessages();
        } else {
          this.#hasConnectedBefore = true;
        }
      }
    }
  }

  async #removeLocalStorageItemsByKey(key: string) {
    removeLocalStorageItems();
    this.#siaSessionService.weakMemoryClearByContains(key);
    await this.#siaCacheDatabaseService.clearAllResponsesContaining(key);
  }

  sendPortalMessage(recipientId: string, messageTypeId: string, data: any) {
    const message: SiaSignalrMessage = {
      recipientId,
      messageTypeId,
      jsonMessage: JSON.stringify(data),
    };
    this.#signalrApi.SendCustomMessage(message);
    this._serviceMessage$.next(message);
  }

  #manageIncomingMessage(message: SiaSignalrMessage) {
    if (!message) return;
    const type = message.messageTypeId?.toLowerCase();
    if (type === 'refreshdata') {
      this.sendRefreshMessages(message);
    } else if (type === 'refreshcollectionsdata') {
      this.sendRefreshCollectionMessages(message);
    } else {
      this._serviceMessage$.next(message);
    }
  }

  sendRefreshCollectionMessages(message: SiaSignalrMessage) {
    if (!message.recipientId) return;
    this.#memoryToClearKeys[message.recipientId] = true;
    clearTimeout(this.#refreshCollectionsTimeout);
    this.#refreshCollectionsTimeout = setTimeout(async () => {
      const keys = Object.keys(this.#memoryToClearKeys);
      const clearPromises: Promise<any>[] = [];

      for (const key of keys) {
        clearPromises.push(this.#removeLocalStorageItemsByKey(key));
        clearPromises.push(
          this.#siaCacheDatabaseService.clearAllStoresContaining(key),
        );
        this.#memoryToNotifyKeys[key] = true;
      }

      await Promise.all(clearPromises);
      this.#memoryToClearKeys = {};

      this.#refreshCollectionsMessage$.next(
        jsonDeepClone(this.#memoryToNotifyKeys),
      );
      this.#memoryToNotifyKeys = {};
    }, 100);
  }

  sendRefreshMessages(message: SiaSignalrMessage) {
    const componentId = message.recipientId;
    if (!componentId) return;
    this.#refreshMessages[componentId] = true;
    clearTimeout(this.#refreshTimeout);
    this.#refreshTimeout = setTimeout(async () => {
      this.#refreshMessage$.next(jsonDeepClone(this.#refreshMessages));

      const keys = Object.keys(this.#refreshMessages);
      const clearPromises: Promise<any>[] = [];

      for (const key of keys) {
        clearPromises.push(this.#removeLocalStorageItemsByKey(key));
        this.#siaSessionService.weakMemorySet(key + '-updated-wm-data', false);
      }

      await Promise.all(clearPromises);
      this.#refreshMessages = {};
    }, 100);
  }

  #buildConnection(userId: number, apiUrl: string) {
    this.#connection = new HubConnectionBuilder()
      .withUrl(`${apiUrl}/base-hub`, {
        transport: HttpTransportType.WebSockets | HttpTransportType.LongPolling,
        skipNegotiation: false,
        accessTokenFactory: () =>
          this.#siaTokenStoreService.getAccessToken() ?? '',
      })
      .withAutomaticReconnect([0, 2000, 10000, 30000])
      .configureLogging(LogLevel.Information)
      .build();
    this.#connection.keepAliveIntervalInMilliseconds = 30000;
    this.#connection.serverTimeoutInMilliseconds = 60000;
    this.#connection.on('ServiceMessage', (message: SiaSignalrMessage) =>
      this.#manageIncomingMessage(message),
    );
    this.#connection.on(
      'UserNotification',
      (message: SiaProfileNotification) => {
        this._profileNotifications$.next(message);
      },
    );
    this.#connection.onreconnecting(() =>
      this._status$.next({ date: new Date(), status: 'warning' }),
    );
    this.#connection.onreconnected(async () => {
      this._status$.next({ date: new Date(), status: 'info' });
      await this.#connection?.invoke('Login', userId);
      this.sendAllMessages();
    });
    this.#connection.onclose((error) => {
      this._status$.next({ date: new Date(), status: 'error' });
      if (error) console.error('SignalR closed with error:', error.message);
      this.#connection = null;
    });
  }

  sendAllMessages() {
    let message: SiaSignalrMessage = {
          recipientId: '*',
          messageTypeId: 'signalr-reconnected',
          jsonMessage: '',
        };
    this._profileNotifications$.next({
      notificationType: message.messageTypeId!,
      count: 0,
    });
    this.sendRefreshMessages(message);
    this.sendRefreshCollectionMessages(message);
    this._serviceMessage$.next(message);
  }

  async initService(userId: number) {
    this.#userId = userId;
    await this.#maybeConnect();
    this.user.set(this.#siaUsersService.getUserData());
    this.checkState();
  }

  async reconnect(userId: number) {
    this.#userId = userId;
    this.#justLoggedIn = true;
    if (this.#connection) {
      try {
        await this.#connection.stop();
      } catch {}
      this.#connection = null;
    }
    await this.#maybeConnect();
  }

  emitLoggedUser(user: SiaTokenUser | null) {
    this.user.set(user);
  }

  clearPostLoginRefreshGuard() {
    this.#justLoggedIn = false;
  }

  async connect(userId: number) {
    if (!userId) return;
    this.#userId = userId;
    await this.#maybeConnect();
    this.user.set(this.#siaUsersService.getUserData());
  }

  async disconnect(userId: number): Promise<void> {
    this.clearPostLoginRefreshGuard();
    this.emitLoggedUser(null);
    if (!this.#connection) return;
    await this.#connection.invoke('Login', userId);
    await this.#connection.stop();
  }

  checkState() {
    if (!this.#connection) {
      this._status$.next({ date: new Date(), status: 'error' });
      return;
    }
    switch (this.#connection.state) {
      case HubConnectionState.Connected:
        this._status$.next({ date: new Date(), status: 'info' });
        break;
      case HubConnectionState.Connecting:
      case HubConnectionState.Disconnecting:
      case HubConnectionState.Reconnecting:
        this._status$.next({ date: new Date(), status: 'warning' });
        break;
      case HubConnectionState.Disconnected:
        this._status$.next({ date: new Date(), status: 'error' });
        break;
    }
  }

  getState() {
    if (!this.#connection) return undefined;
    return this.#connection.state;
  }

  getApiUrl(): string | null | undefined {
    const apiUrls = window.sessionStorage.getItem('APIURLS');
    if (!apiUrls) return null;
    const urls: { id: string; value: string }[] = JSON.parse(apiUrls);
    if (!urls || urls.length === 0) return null;
    return urls.find((x) => x.id === 'LOCALURL')?.value;
  }
}
